import { Typography } from '../../Typography';

export interface EntitySummaryFieldProps {
  /**
   * The field label to display
   */
  label: string;
  /**
   * The formatted field value to display (pre-formatted string or number)
   * Returns null if value is empty or whitespace only
   */
  value: string | number | undefined;
  /**
   * Optional unit suffix to append (e.g., 'M', 'CM', 'FT')
   * Will be uppercased and displayed after the value
   */
  unit?: string;
}

/**
 * EntitySummaryField - Displays a single label/value pair in the entity summary
 *
 * Pure presentation component that accepts pre-formatted values.
 * Returns null if the value is empty, allowing consumers to handle
 * empty fields gracefully.
 *
 * @example
 * ```tsx
 * <EntitySummaryField label="Species" value="Red Oak" />
 * // Renders: "Species: Red Oak"
 * ```
 *
 * @example
 * ```tsx
 * <EntitySummaryField label="Height" value="10.5" unit="M" />
 * // Renders: "Height: 10.5 M"
 * ```
 */
export const EntitySummaryField = ({ label, value, unit }: EntitySummaryFieldProps) => {
  // Convert value to string for processing
  const displayValue = value !== undefined ? String(value) : '';

  // Return null if displayValue is empty or whitespace only
  if (!displayValue || !displayValue.trim()) {
    return null;
  }

  // Format unit suffix (uppercase, with leading space)
  const unitSuffix = unit && unit.trim() ? ` ${unit.toUpperCase()}` : '';

  const ariaLabel = `${label}: ${displayValue}${unitSuffix}`;

  return (
    <Typography variant="body1" aria-label={ariaLabel} sx={{ lineHeight: '18px' }}>
      {label}:{' '}
      <strong>
        {displayValue}
        {unitSuffix}
      </strong>
    </Typography>
  );
};

EntitySummaryField.displayName = 'EntitySummaryField';
